export type BlogPost = {
  slug: string
  title: string
  description: string
  category: string
  publishedAt: string
  relatedPath:
    | "/tools/youtube-to-shorts"
    | "/tools/twitch-vod-to-shorts"
    | "/tools/gameplay-facecam-to-shorts"
    | "/tools/long-video-to-short-video"
  relatedLabel: string
  sections: {
    id: string
    title: string
    paragraphs: string[]
    steps?: string[]
  }[]
}

export const blogPosts: BlogPost[] = [
  {
    slug: "how-to-turn-long-videos-into-shorts",
    title: "How to Turn Long Videos into Shorts",
    description:
      "Learn how to convert a long video into shorts by choosing complete moments, setting a vertical format, reviewing captions, and checking every cut before export.",
    category: "Long video to shorts",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/long-video-to-short-video",
    relatedLabel: "Turn a long video into shorts",
    sections: [
      {
        id: "choose-a-source",
        title: "Choose a source you can reuse",
        paragraphs: [
          "Start with a video file or a supported public YouTube video or Twitch VOD. You need permission to process and reuse the footage. A public link does not grant those rights, and a link can still fail when its source is restricted, unavailable, or protected.",
          "To repurpose long-form video, first find moments that can stand alone, then shape each one for a short-form viewer. Cutting the recording into equal intervals skips the selection and often leaves clips without a point.",
        ],
        steps: [
          "Open the intoshort dashboard and choose a video file or paste a supported public video link.",
          "For a link, wait for the source preview, then continue to the clip settings. For a file, check its name and preview in the configuration dialog.",
        ],
      },
      {
        id: "define-the-moment",
        title: "Decide what makes a moment worth keeping",
        paragraphs: [
          "Choose the kind of passage that suits the source. An interview may contain a complete answer, a stream may have a reaction and the event that caused it, and a tutorial may have one useful explanation. Add context about the topic or audience when it helps guide the search.",
          "In intoshort, choose Strongest overall or one or more moment categories such as Funny, Surprising Facts, or Stories. These categories are alternatives. A passage can fit one category without fitting the others. Set a duration and a target count, or leave the count on Auto. A target guides the search; it does not guarantee that the source contains that many strong clips.",
        ],
        steps: [
          "Choose the moment types, target duration and desired clip count, then enable captions if you want them in the first renders.",
          "Select Generate and follow the processing stages on the video page. For a local file, keep the tab open until its upload is confirmed.",
          "When the drafts are ready, play each short before deciding which ones to edit or export.",
        ],
      },
      {
        id: "check-a-cut",
        title: "Check the setup, payoff, and meaning",
        paragraphs: [
          "For example, imagine a creator saying, 'We replaced the camera because the old one overheated during every stream.' A cut that starts at 'the old one overheated' may leave a new viewer asking what changed. Including the sentence about replacing the camera gives the short a clearer setup. This is an illustrative example, not a result from an intoshort job.",
          "Play each proposed passage from beginning to end. Keep enough context to explain the event or answer, and listen just beyond the cut to make sure you have not removed a qualification. Remove repeated or weak suggestions instead of filling the requested count with clips that do not stand alone.",
        ],
        steps: [
          "State the point of the clip in one sentence.",
          "Watch it as if you have not seen the source video.",
          "Keep the shortest setup that makes the point understandable.",
          "Check that the ending preserves the speaker’s meaning or the event’s outcome.",
        ],
      },
      {
        id: "set-the-short-format",
        title: "Choose a format and check the framing",
        paragraphs: [
          "Choose a 9:16 vertical export for a full-height short, then review what the crop keeps in frame. A horizontal source may need a centered crop, a blurred background, or a gameplay and facecam layout when the source supports it. The automatic layout can fall back to a centered crop when facecam detection is absent or unstable, so inspect the result scene by scene.",
          "If vertical framing hides useful gameplay or a second person, consider whether a different layout or a 16:9 export better serves the passage. A vertical frame is a format choice, not a reason to lose the detail that explains the clip.",
        ],
      },
      {
        id: "review-captions",
        title: "Review captions before exporting",
        paragraphs: [
          "Check caption wording against the audio, especially for names, numbers, game terms, and short words that can change a sentence’s meaning. Adjust the caption style, size, margins, or position if the text covers a face or important on-screen action. Captions can make speech easier to follow, but they cannot restore missing context or correct an unclear source by guessing.",
          "The editor works on an individual short. Save your cut and caption changes, wait for the new render, then watch that rendered version before downloading it. The export is not updated merely by previewing an edit.",
        ],
      },
      {
        id: "know-what-automation-misses",
        title: "Know what the first pass can miss",
        paragraphs: [
          "Transcript-led selection may miss a brief silent play, a visual joke, overlapping speech, or context that only appears on screen. Very long or high-resolution sources can also take more time to process, and poor audio can reduce transcription quality or stop the job.",
          "Treat each suggestion as an editable draft. Keep the original source, reject moments that do not work on their own, and export only the versions you have reviewed. Neither a requested clip count nor an AI selection guarantees a useful result or audience response.",
        ],
      },
    ],
  },
  {
    slug: "how-to-choose-short-clips-from-long-videos",
    title: "How to Choose Short Clips from Long Videos",
    description:
      "Find moments that work on their own, keep the context that matters, and reject weak cuts before spending time on captions and effects.",
    category: "Clip selection",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/long-video-to-short-video",
    relatedLabel: "Turn a long video into shorts",
    sections: [
      {
        id: "choose-a-complete-moment",
        title: "Choose a complete moment, not a time window",
        paragraphs: [
          "A short can begin and end cleanly and still leave the viewer wondering what happened. This often happens when the cut follows a duration target instead of an idea. The speaker finishes a sentence, but the answer depends on a question that was left behind.",
          "Start by naming what the viewer gets from the passage. It might explain one mistake, capture a funny reversal, or show a difficult play and the reaction that follows. If you need several sentences to explain why the moment matters, check whether you have selected too much or left out essential context.",
        ],
      },
      {
        id: "watch-without-the-episode",
        title: "Watch as someone who missed the full video",
        paragraphs: [
          "Imagine an interview answer beginning with, 'That was the expensive part.' The speaker knows what 'that' refers to. A new viewer does not. Moving the start back to include the specific decision may produce a longer but more understandable clip.",
          "Gameplay has the same problem. A celebration can be entertaining, but the audience may need to see the failed attempt, the last opponent, or the unexpected result to understand it. Keep the smallest amount of setup that makes the reaction meaningful. Do not assume captions can repair footage that omits the event itself.",
        ],
      },
      {
        id: "check-opening-and-ending",
        title: "Check the opening and ending together",
        paragraphs: [
          "An opening creates an expectation. The rest of the clip needs to satisfy it. If someone begins explaining why they changed their recording setup, the cut should include the reason, rather than stopping just before it arrives.",
          "Listen beyond your proposed end point before trimming. A sentence such as 'but only when we record outdoors' can change the advice that came before it. Keep qualifications, corrections, and reactions that affect the meaning, even when removing them would make the clip sound more dramatic.",
        ],
      },
      {
        id: "compare-the-candidates",
        title: "Compare candidates before polishing them",
        paragraphs: [
          "Review the proposed clips together. Two passages can use different words while making the same point. Keep both only when each offers something distinct, such as an explanation in one and a useful demonstration in the other.",
          "You do not need to publish every suggestion. A source with two clear moments is better represented by two reviewed clips than by a larger batch padded with unfinished stories. A requested clip count helps organize the search; it does not prove that the source contains that many worthwhile passages.",
        ],
      },
      {
        id: "review-in-intoshort",
        title: "Review your first cut in intoshort",
        paragraphs: [
          "In intoshort, choose Strongest overall or the moment categories that fit your source. Add context about the audience or the kind of passage you want. When the suggestions are ready, watch them before changing their appearance. AI selection is a first pass, and short visual events can still be missed.",
        ],
        steps: [
          "Describe the point of each candidate in one sentence.",
          "Play it without relying on your memory of the full source.",
          "Open Edit to refine the boundaries while preserving the original meaning.",
          "Remove weak or repetitive suggestions, then review the saved exports you intend to share.",
        ],
      },
    ],
  },
  {
    slug: "how-to-find-hooks-for-short-videos",
    title: "How to Find Hooks for Short Videos in Your Existing Footage",
    description:
      "Choose a clear opening from the footage you already have. Use questions, surprises, and reactions without changing what the speaker meant.",
    category: "Editing",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/twitch-vod-to-shorts",
    relatedLabel: "Find moments in a Twitch VOD",
    sections: [
      {
        id: "start-with-a-reason-to-watch",
        title: "Give the viewer a reason to watch",
        paragraphs: [
          "The first frame of a short is rarely the first frame of the original recording. A stream may start with greetings and an interview may start with introductions. A useful clip can begin much later, when something specific is happening.",
          "A hook tells the viewer what deserves their attention. It can be a question, a visible problem, or a reaction that makes sense immediately. It does not have to be loud. A speaker calmly admitting a precise mistake can give the audience more reason to continue than a generic promise that something incredible is coming.",
        ],
      },
      {
        id: "find-an-existing-question",
        title: "Keep the question when the answer needs it",
        paragraphs: [
          "An answer beginning with 'about three weeks' needs its question. An answer beginning with 'we stopped recording in that room because of the echo' may already contain enough context. Listen to both versions before deciding whether to keep the interviewer.",
          "Choose questions that the selected passage actually answers. If the guest explains one way to reduce setup time, do not package it as a complete guide to building a studio. The opening should describe the value present in the clip.",
        ],
      },
      {
        id: "look-for-a-change",
        title: "Look for a change the audience can follow",
        paragraphs: [
          "A useful opening often sits where expectations change. Someone describes a plan that failed, realizes they misunderstood a rule, or sees an unlikely outcome in a game. The change creates a question that the remaining footage can resolve.",
          "For example, 'I thought the microphone was broken' introduces a concrete problem. It works as an opening only if the passage goes on to explain what happened. An unresolved setup leaves the viewer waiting for a clip you did not include.",
        ],
      },
      {
        id: "check-visual-and-spoken-context",
        title: "Make the first image support the opening",
        paragraphs: [
          "For gameplay, inspect what the audience sees when the short begins. If the streamer reacts to an event outside the crop, the opening may be confusing even with accurate captions. Start early enough to show the event or choose a composition that preserves it.",
          "For a spoken explanation, read the first caption while looking at the frame. Check that the text identifies the subject and does not cover the detail being discussed. Extra zooms, sounds, and animated words are optional. They cannot supply a missing explanation.",
        ],
      },
      {
        id: "tighten-without-rewriting",
        title: "Tighten the opening without rewriting the source",
        paragraphs: [
          "In intoshort, use the editor to trim a greeting or remove a repeated lead-in. Keep enough audio before the first word to avoid clipping its beginning. Preserve the order of the retained footage and replay the result at normal speed.",
          "Check the full statement before approving a sharper cut. Removing 'I used to think' can turn a belief the speaker rejected into a claim they appear to endorse. Caption corrections should reflect the words that were spoken, rather than invent a stronger sentence.",
          "Compare your original and revised opening. Keep the revision when a new viewer can understand the subject sooner and the rest of the clip still delivers what the beginning suggests. No opening can guarantee views, but you can check whether it is clear and faithful before publishing.",
        ],
      },
    ],
  },
  {
    slug: "how-to-fix-ai-captions-before-publishing",
    title: "How to Add and Fix Captions on Shorts",
    description:
      "Learn how to add captions to shorts and fix AI captions before publishing. Check wording, timing, readability, and placement in the intoshort editor.",
    category: "Captions",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/long-video-to-short-video",
    relatedLabel: "Create and edit shorts from a long video",
    sections: [
      {
        id: "add-captions-to-shorts",
        title: "Add captions when you generate your shorts",
        paragraphs: [
          "In intoshort, captions are generated from the source audio as part of creating shorts. Start with a video file or a supported public YouTube or Twitch VOD link. Enable captions in the clip settings so the first rendered drafts include them.",
        ],
        steps: [
          "Add your source video, then open its clip settings.",
          "Enable captions, choose a caption style and review the selection settings before choosing Generate.",
          "When processing finishes, open a short with Edit and select Captions to check the text and appearance.",
        ],
      },
      {
        id: "check-the-words-first",
        title: "Check the words before choosing a style",
        paragraphs: [
          "An automatic caption can look convincing while getting a name or number wrong. Read it while listening to the source. Pay particular attention to usernames, game terminology, product names, quantities, and short words such as 'not' that can reverse a statement.",
          "In intoshort, open the short's editor and use Captions to review its text. Correct the wording to match the audio. Do not turn an uncertain transcription into a confident guess. If speech is unclear, replay the surrounding source before deciding what belongs on screen.",
          "Caption edits belong to the short; the original transcription remains separate. That distinction lets you correct a deliverable without treating the corrected text as a new recording of what happened.",
        ],
      },
      {
        id: "listen-for-caption-boundaries",
        title: "Listen for early and late captions",
        paragraphs: [
          "When you need to fix captions on shorts, find the exact cue that differs from the audio before changing it. A missing or mistimed cue may need a timing adjustment or removal; a misheard word needs a text correction that matches the recording. Do not use caption editing to paraphrase the speaker.",
          "Watch a passage at normal speed instead of relying only on a paused timeline. Does the text appear with the speech? Does it disappear before you can finish reading? Does a caption from the previous speaker remain visible over the next reply?",
          "The subtitle track in intoshort lets you move, trim, or remove caption cues. Use these controls for the specific issue you can hear and see. A correction to a displayed cue does not regenerate the source's word alignment. Review animated styles as well as the cue boundaries, especially around interruptions and fast speech.",
        ],
      },
      {
        id: "read-at-phone-size",
        title: "Read at the size your audience will see",
        paragraphs: [
          "Large text is not automatically easier to follow. A long caption can cover the facecam, split a phrase awkwardly, or force the viewer to read while missing the action. Small text may look tidy on a desktop monitor and become difficult to read on a phone.",
          "Adjust the caption size and Maximum words on screen in intoshort. Try a short passage with both quick and slow speech before applying a style more widely. Choose emphasis that helps the viewer follow the sentence. If every word competes for attention, a simpler treatment may be easier to watch.",
        ],
      },
      {
        id: "check-placement-after-framing",
        title: "Check placement after changing the frame",
        paragraphs: [
          "Caption placement and video composition affect each other. A position that works over a talking head may cover a health bar, an item description, or the streamer's expression in a gaming layout.",
          "Move captions where the words remain readable without hiding essential content. Check each scene rather than assuming one position works throughout the short. Leave room for the destination app's controls and inspect the result in the viewing context where you plan to share it.",
        ],
      },
      {
        id: "review-the-saved-version",
        title: "Review the saved export",
        paragraphs: [
          "Use Save for this short to apply your edits and wait for rendering to finish. Then watch the new export. A previous rendered version can remain visible while the replacement is being prepared, so confirm that you are reviewing the updated file.",
        ],
        steps: [
          "Listen once for names, numbers, and meaning.",
          "Watch once for timing and changes of speaker.",
          "Read the captions on a phone and check that they do not hide the action.",
          "Approve the finished export only after your corrections appear in it.",
        ],
      },
    ],
  },
  {
    slug: "how-to-reframe-horizontal-video-for-vertical-shorts",
    title: "How to Reframe Horizontal Video for Vertical Shorts",
    description:
      "Keep the speaker, gameplay, and essential context visible when turning a wide video into a vertical short. Review the composition scene by scene.",
    category: "Reframing",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/gameplay-facecam-to-shorts",
    relatedLabel: "Explore gameplay and facecam framing",
    sections: [
      {
        id: "decide-what-must-stay-visible",
        title: "Decide what must stay visible",
        paragraphs: [
          "A wide recording has room for details that a vertical crop can remove. Before moving the frame, decide which details explain the moment. In an interview, that might be the person speaking and the object they hold. In a stream, it might be an opponent near the edge of the gameplay and the creator's reaction.",
          "Pause at the key moment and name what the viewer needs to see. Then play the whole passage. A crop that works on one still image may lose the subject when they move or hide the result of an action a few seconds later.",
        ],
      },
      {
        id: "choose-a-composition",
        title: "Choose the composition for the footage",
        paragraphs: [
          "A close vertical crop can work when one subject stays in a small area. When important details span the full image, preserving the wide frame may be clearer, even if the subject appears smaller. A blurred background can fill the remaining canvas, but it does not make small source details easier to read.",
          "For gameplay with a facecam, a stacked layout can keep the reaction and game visible together. Check the balance at phone size. Giving the facecam more space also gives the gameplay less space, which can make distant opponents, menus, or subtitles harder to follow.",
        ],
      },
      {
        id: "review-automatic-framing",
        title: "Treat automatic framing as a draft",
        paragraphs: [
          "intoshort can propose a gameplay and facecam composition for supported vertical processing. Detection can be uncertain, and an inset picture is not necessarily a human webcam. When no sufficiently stable region is available, or the framing service fails, the result can use a centered crop.",
          "Watch for missing faces, overlays mistaken for the facecam, and action outside the selected region. An exported file can be technically valid while showing the wrong part of the scene. If the suggested composition hides why the moment matters, revise it before sharing.",
        ],
      },
      {
        id: "adjust-scenes-in-intoshort",
        title: "Adjust the frame scene by scene in intoshort",
        paragraphs: [
          "Open Reframing in the short's editor. Manual framing controls require multiple existing scenes. If scene detection is missing, use Detect scenes where offered and review the proposed boundaries before editing. Detection itself does not replace the rendered export.",
          "Select the scene and adjust the available source rectangles. The central preview shows the draft composition. Review the beginning and end of that scene, then check the transition into the next one. A scene crop should not be assumed to track every movement inside the scene.",
          "Apply layout to all scenes can reuse the active composition, but review the other scenes before saving. A facecam can move, disappear, or be replaced by a different screen. Reusing one layout is convenient only when it still fits the footage.",
        ],
      },
      {
        id: "check-captions-and-export",
        title: "Check captions and the finished export together",
        paragraphs: [
          "After changing a layout, review the caption position again. Keep the text clear of the subject and any game information the audience needs. Account for the controls that the destination platform places over the video.",
          "Save the short, wait for the new render, and watch the finished file on a phone. Check the action, the reaction, and every scene transition. Keep a composition because it makes the moment understandable, rather than because it fills every part of the vertical canvas.",
        ],
      },
    ],
  },
  {
    slug: "how-to-keep-video-quality-when-exporting-shorts",
    title: "How to Keep Video Quality When Exporting Shorts",
    description:
      "Understand source resolution, cropping, and compression so you can spot quality problems before and after exporting a short from intoshort.",
    category: "Export quality",
    publishedAt: "2026-10-26",
    relatedPath: "/tools/gameplay-facecam-to-shorts",
    relatedLabel: "Prepare gameplay and facecam shorts",
    sections: [
      {
        id: "start-with-the-source",
        title: "Check the source before blaming the export",
        paragraphs: [
          "If a short looks soft, open the original recording at the same moment. Look at the face, small text, and moving objects. An export cannot recover detail that was already missing from a heavily compressed recording.",
          "Use an original file you are allowed to process when you have it, rather than a copy that has been downloaded and exported through several editors. Keep that source available while reviewing the result. It gives you a reference for deciding whether the problem started in the recording, the crop, or the final delivery.",
        ],
      },
      {
        id: "understand-the-crop",
        title: "A tighter crop uses less of the source image",
        paragraphs: [
          "Turning a horizontal recording into a close vertical shot keeps only part of its width. Enlarging that region can make the subject easier to see, but it also exposes softness and compression that were less noticeable in the full image.",
          "A higher output resolution does not mean every detail came from a source of that resolution. If a facecam occupied a small corner of the original stream, making it a large part of the short cannot create additional facial detail. Try a less aggressive crop or a different layout when enlargement makes the result distracting.",
        ],
      },
      {
        id: "choose-source-quality-in-intoshort",
        title: "Choose source quality deliberately in intoshort",
        paragraphs: [
          "For supported YouTube and Twitch links, the HD control shows source resolutions detected for that video. Imports use a maximum of 1080p by default. Higher detected options can take longer to process, and selecting one sets a ceiling rather than guaranteeing that the same format will remain available later.",
          "A higher-resolution source can provide more detail for a crop when the recording actually contains it. It will not fix motion blur, unreadable overlays, or an already compressed facecam. Uploaded files retain their original source file rather than being replaced by a link's quality setting.",
          "intoshort offers 9:16 and 16:9 exports. Choose the format for the intended composition, then inspect the saved result. Format, source quality, and framing are separate decisions; changing one does not automatically solve problems in the others.",
        ],
      },
      {
        id: "inspect-difficult-moments",
        title: "Inspect motion and small text",
        paragraphs: [
          "Do not judge an export only by a paused talking-head shot. Fast camera movement, detailed backgrounds, and small interface text can reveal problems that a still frame hides. For gameplay, include a busy fight or rapid turn in your review.",
          "Check captions over both bright and dark scenes. Thin lettering or a weak contrast choice may become difficult to read after the destination platform processes the upload. If you change the caption treatment, return to the same difficult moments to compare the result.",
        ],
      },
      {
        id: "compare-the-delivery-stages",
        title: "Compare the source, export, and published playback",
        paragraphs: [
          "First compare the source with the finished intoshort export. If the exported file looks good locally but the published version looks worse, investigate the platform's processing state, playback quality, and connection before changing the edit. A platform may create its own versions of an uploaded file.",
          "Keep the approved export as your reference. If you need another edit, return to the project and source rather than using a compressed social download as the new master. Review the final playback on the device your audience uses, with particular attention to faces, captions, and the action that makes the clip worth watching.",
        ],
      },
    ],
  },
  {
    slug: "how-to-turn-youtube-videos-into-shorts",
    title: "How to Turn YouTube Videos into Shorts",
    description:
      "Learn how to make shorts from existing YouTube videos: import an eligible public link, review suggested moments, refine captions and cuts, and download approved shorts.",
    category: "YouTube",
    publishedAt: "2026-10-23",
    relatedPath: "/tools/youtube-to-shorts",
    relatedLabel: "YouTube to Shorts",
    sections: [
      {
        id: "before-you-start",
        title: "Before you start",
        paragraphs: [
          "If you want to make shorts from an existing video, first decide whether the source can be reused. This guide uses an eligible public YouTube video as its example; the same selection and review principles apply to other supported long-form sources.",
          "You need a direct link to a public YouTube video that you have the right to download, edit, and reuse. A public link does not grant those rights. Private, age-restricted, region-blocked, protected, or otherwise unavailable videos may not import. Playlists and links that do not resolve to an accessible video are not supported sources.",
          "intoshort prepares editable drafts. Importing a video does not publish anything to YouTube, and the selection is not a promise of a particular number of usable shorts or audience performance. You remain responsible for choosing moments that make sense in context.",
        ],
      },
      {
        id: "add-youtube-link",
        title: "Add the YouTube link",
        paragraphs: [
          "Open the dashboard at /app and paste the video URL. Use the video’s regular watch link, or a Shorts link when its underlying video is accessible. The app checks the source and reads the information available for configuration. If the source cannot be read, resolve that access issue or use a video file you are permitted to process.",
        ],
        steps: [
          "Copy the URL of the public YouTube video.",
          "Paste it into the source field and wait for the source details to load.",
          "If the preview is unavailable or incomplete, do not assume processing will succeed; check the video’s availability and try again later or choose an authorized file.",
        ],
      },
      {
        id: "configure-selection",
        title: "Describe the shorts you want",
        paragraphs: [
          "Before processing, choose the editorial direction and output settings. You can ask for the strongest overall moments or select moment types such as funny, surprising facts, or stories. These categories are alternatives: a strong moment does not have to fit several at once. Add context in your own words when the topic or audience needs explanation.",
          "Choose a target duration, a short count or Auto, caption treatment, and source quality where available. Auto estimates roughly one short for every five minutes of source, but it is not a quota. The number of useful moments depends on the video, and weak passages should not be added just to fill a target.",
        ],
        steps: [
          "Choose one overall selection mode or a combination of moment types.",
          "Set the context, target duration, desired count, captions, and quality.",
          "Review the settings before starting; processing can take time and uses the configured video and AI services.",
        ],
      },
      {
        id: "process-and-review",
        title: "Let intoshort prepare a first cut, then review it",
        paragraphs: [
          "The processing pipeline transcribes the source, analyzes the requested moments, and calculates cuts from referenced parts of the video. intoshort keeps selected passages in their original order; it does not split the entire source into fixed-length pieces. A transcript can miss visual context, jokes that depend on what came before, or a moment that works without speech, so treat each suggestion as an editorial draft.",
          "When processing finishes, open the video page and play every suggested short. Check that the opening gives enough context, the ending feels intentional, and the cut does not interrupt a sentence or reaction. Remove suggestions that do not stand on their own. Results may be fewer than your requested count when the source has too few strong moments or a render fails.",
        ],
      },
      {
        id: "edit-captions-and-cuts",
        title: "Refine cuts and captions",
        paragraphs: [
          "Open Edit on a short to adjust its cut and caption appearance. You can trim the ends, split or remove portions, and change caption style, size, margins, or position. Keep the original meaning and timing intact when correcting caption text. Preview the result after each meaningful adjustment; a technically clean cut can still change the sense of a quote if it removes the surrounding context.",
        ],
        steps: [
          "Open the short you want to correct and use the editor preview to locate the issue.",
          "Adjust the cut or caption treatment, then save the short to apply the edits in a new render.",
          "Wait for the saved version to finish rendering and review that version before downloading.",
        ],
      },
      {
        id: "download-and-troubleshoot",
        title: "Download approved shorts and handle common issues",
        paragraphs: [
          "Download only the versions you have reviewed and want to use. The app prepares files for export; it does not publish them merely because processing or rendering completed. A downloaded short is still subject to YouTube’s format, copyright, and account requirements when you upload it yourself.",
          "If a link fails, first check whether the source is public and playable from your location, and whether it is a direct video URL. A successful preview does not guarantee that a later full download or render will complete. If a result has an awkward cut, incorrect caption, or missing visual context, edit it or discard it rather than presenting the AI selection as final.",
        ],
      },
    ],
  },
  {
    slug: "how-to-turn-twitch-vods-into-clips",
    title: "How to Turn Twitch VODs into Shorts and Stream Highlights",
    description:
      "Learn how to turn a public Twitch VOD into shorts and stream highlights: review selected moments, correct the edit, and download approved clips in intoshort.",
    category: "Twitch",
    publishedAt: "2026-10-23",
    relatedPath: "/tools/twitch-vod-to-shorts",
    relatedLabel: "Twitch VOD to Shorts",
    sections: [
      {
        id: "choose-eligible-vod",
        title: "Choose a VOD intoshort can process",
        paragraphs: [
          "To turn a Twitch VOD into shorts or stream highlights, start with a completed public VOD that you have the right to download, edit, and reuse. intoshort proposes selected passages for review; it does not divide the full stream into fixed-length chunks.",
          "Start with a public, completed Twitch VOD that you are allowed to download, edit, and reuse. The supported link points directly to a video, for example https://www.twitch.tv/videos/123456789. A channel page, live stream, Twitch clip, or VOD that requires a login or subscription is not an eligible source. Public availability alone does not provide reuse rights.",
          "The importer processes the whole VOD. Share parameters and timestamps in the link are stripped, so adding a time offset does not limit processing to that portion. If you only have permission to reuse one part, make sure your rights cover the processing and intended reuse of the source before continuing.",
        ],
      },
      {
        id: "import-twitch-vod",
        title: "Import the VOD",
        paragraphs: [
          "Open the dashboard at /app, paste the direct VOD URL, and wait for the source details before configuring your clips. intoshort creates a video job and uses the same download, storage, transcription, and clip workflow as other supported sources. Processing depends on the app’s video services being available; validating a URL is not the same as completing a full download or render.",
        ],
        steps: [
          "Copy the direct URL from the completed VOD’s video page.",
          "Paste the URL into the source field and confirm that it is a /videos/ link.",
          "Continue to the clip settings before starting processing.",
        ],
      },
      {
        id: "set-clip-direction",
        title: "Set the direction for the first pass",
        paragraphs: [
          "Choose what kind of moments to look for, add context about the game or stream, and set a target duration and short count. You can select strongest overall moments or request categories such as funny, surprising facts, or stories. The system looks for worthwhile passages; it does not turn every minute into a clip or fill a quota with weak material. A target guides the search but cannot guarantee that many useful results.",
          "Review caption and output settings as well. For a stream, context can help distinguish an in-game callout from a conversation with chat, but the transcript may not capture visual actions or why a reaction matters. The creator still decides whether a moment works without the surrounding stream.",
        ],
        steps: [
          "Choose an overall or category-based selection direction.",
          "Describe useful context, such as the game or the type of reaction that matters.",
          "Set the duration, target count, caption options, and quality before starting.",
        ],
      },
      {
        id: "review-suggestions",
        title: "Review the selected moments",
        paragraphs: [
          "After processing, play each proposed clip from beginning to end. Check whether the setup is understandable, whether the reaction follows the event, and whether chat or game audio remains meaningful. The suggestions are selected passages from the VOD in source order, not fixed chunks. Remove duplicates, slow stretches, or moments that depend on context a viewer would not have.",
          "Do not assume a timestamp in the Twitch URL was honored: imports cover the full VOD. Likewise, do not assume every notable on-screen event has been detected from speech alone. If the selected set misses a brief visual moment, you can reject it or choose another editing approach where the editor allows it.",
        ],
      },
      {
        id: "edit-and-save",
        title: "Correct the edit and save it",
        paragraphs: [
          "Use the short editor to trim or split a passage, remove unwanted sections, and refine captions. Check speaker changes, game terms, and names carefully because automatic transcription may mishear them. Adjust caption style and placement so text does not obscure important gameplay or the streamer’s face. Preview the complete edit after your changes.",
        ],
        steps: [
          "Open Edit on the selected short and inspect its cut and captions.",
          "Make only the corrections needed to preserve the event and its context.",
          "Use Save for this short and wait for the updated render to complete.",
        ],
      },
      {
        id: "download-and-common-errors",
        title: "Download the clips you approve",
        paragraphs: [
          "Review the rendered version, then download the clips you approve for your own publishing workflow. Importing, editing, or downloading a VOD does not publish a clip to Twitch or another platform. Before posting, check the source rights and the destination platform’s rules.",
          "If import fails, verify that the link is a completed public /videos/ VOD rather than a channel, live, or clip URL, and that it does not require subscriber access. If a timestamp was included, remember it has no effect on the imported range. If processing succeeds but the result is weak, reduce the target or discard the suggestions; there is no guaranteed clip count or virality outcome.",
        ],
      },
    ],
  },
  {
    slug: "how-to-frame-gameplay-and-facecam",
    title: "How to Turn Gameplay and Facecam into Vertical Shorts",
    description:
      "Learn how to make gameplay into shorts while keeping facecam and action in view. Choose a vertical layout, adjust scene crops, and review the rendered clip.",
    category: "Editing",
    publishedAt: "2026-10-23",
    relatedPath: "/tools/gameplay-facecam-to-shorts",
    relatedLabel: "Gameplay with Facecam",
    sections: [
      {
        id: "understand-reframing",
        title: "Understand what reframing changes",
        paragraphs: [
          "When you make gameplay into shorts, the vertical crop has to keep the event readable as well as the facecam visible. Review the selected passage first, then check each scene’s crop and caption position in the rendered short.",
          "A landscape gameplay recording often needs a different composition for a vertical short. Reframing lets you position and resize gameplay and a detected facecam by scene, while previewing how the source fits the selected export format. It changes the crop and layout; it does not create a facecam where the source has none or guarantee that automatic facecam detection is correct.",
          "The Reframing tab is available for any source and both export formats, but manual scene editing requires multiple existing scenes in the short. If scenes have not been detected, the editor can offer Detect scenes. That action is explicit; opening the editor alone does not run it. Review the source preview and scene boundaries before changing a crop.",
        ],
      },
      {
        id: "choose-layout",
        title: "Choose a starting layout",
        paragraphs: [
          "Select a layout for the active scene. Full centers a vertical frame on the source. Blur keeps the full source image visible over a blurred background, which can preserve a wide gameplay view in a 9:16 export. Gaming places the facecam above gameplay, using 40% of the canvas for facecam and 60% for gameplay. Gaming 2 reverses the order: gameplay takes the upper 60%, with facecam in the lower 40%.",
          "Gaming layouts remain available when no facecam is detected, but that does not mean the app has found one. Review the preview and set a useful facecam frame yourself when needed. Layout choice and crop can vary by scene; an opening screen, gameplay action, and webcam reaction may each need a different composition.",
        ],
        steps: [
          "Open the short in the editor and choose Reframing.",
          "Select a detected scene and choose Full, Blur, Gaming, or Gaming 2.",
          "Check that the important gameplay area and any facecam fit inside the preview.",
        ],
      },
      {
        id: "adjust-scene-crops",
        title: "Adjust gameplay and facecam by scene",
        paragraphs: [
          "The source preview shows the real frame at the playhead. A rectangle marks the gameplay crop; a second rectangle appears for a stable facecam detected in the relevant segment. Move or resize the rectangles in the source preview. Gameplay can be resized freely, while the facecam keeps its original aspect ratio. Keep both rectangles inside the source image and avoid cropping out the game HUD, action, or the person’s face.",
          "A manual facecam rectangle is only a crop setting; it is not a new detection result. If the streamer’s overlay moves, disappears, or changes size between scenes, adjust each affected scene separately. A single crop should not be assumed to track a moving webcam throughout the clip.",
        ],
      },
      {
        id: "preview-layout",
        title: "Check the composition and captions",
        paragraphs: [
          "The central preview reflects the current draft immediately. Scrub through the scene and inspect both its beginning and later frames. Confirm that the crop still contains the key gameplay and facecam as the source changes. Full and Blur center captions at 85% of the scene height; Gaming places them around the 40% split, and Gaming 2 around the 60% split. You can adjust caption position manually when the automatic placement covers something important.",
          "If several scenes should share a layout, Apply layout to all scenes copies the active layout and its crop positions to the retained scenes. Review the result scene by scene before saving; a layout that works for one camera position may hide the action in another.",
        ],
      },
      {
        id: "save-rerender",
        title: "Save the framing and wait for a new render",
        paragraphs: [
          "Framing changes stay in the draft until you save. Choose Save for this short to persist the per-scene rectangles and start the existing asynchronous render. The already-downloaded export does not change when you only preview edits. Once rendering completes, play the new version and confirm that the saved composition matches the editor preview before downloading it.",
        ],
        steps: [
          "Review each changed scene and any caption positions affected by the layout.",
          "Select Save for this short and follow the render status.",
          "Play the newly rendered short from start to finish, then download the approved version.",
        ],
      },
      {
        id: "common-framing-problems",
        title: "Avoid common framing problems",
        paragraphs: [
          "If the Reframing controls cannot be edited, check that the short has multiple detected scenes. If a facecam rectangle is missing, there may be no sufficiently stable detection; choose a layout and set a crop manually only when the source supports it. If gameplay becomes hard to follow, try Blur to preserve the complete wide image or reposition the gameplay crop. If the facecam shifts between scenes, create or use separate scene boundaries and review each crop independently.",
          "The preview is a draft of the selected composition, and saved changes require a rerender to appear in the export. Scene-by-scene framing does not guarantee automatic tracking of movement inside a scene. Keep the original source and review the finished file; do not assume a layout has preserved every important detail without checking it.",
        ],
      },
    ],
  },
]
